import React, { useState } from 'react';
import { Alert, Stack, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { useAuth } from 'contexts/AuthContext';
import { GlassButton } from 'dashboard/components/common';

const CONFIRM = 'CLOSE MY ACCOUNT';

/**
 * Closing your own user account — not a brand.
 *
 * The server refuses with a 409 when you are the sole owner of a brand,
 * naming it, rather than orphaning a live storefront. That message is
 * surfaced verbatim: it tells you exactly which brand and what to do about it.
 */
export default function AccountDangerTab({ onClosed }) {
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState('');
  const { logout, user } = useAuth();

  const matches = confirmText.trim() === CONFIRM;

  const close = () => {
    setBusy(true);
    setBlocked('');
    API.deleteAccount({ user_id: user?._id })
      .then(() => {
        toast.success('Your account has been closed.');
        onClosed?.();
        logout();
        window.location.assign('/');
      })
      .catch((err) => {
        const msg = err?.message || 'Could not close your account';
        // The sole-owner refusal is guidance, not a failure — keep it on
        // screen rather than letting a toast disappear.
        setBlocked(msg);
        toast.error(msg);
      })
      .finally(() => setBusy(false));
  };

  return (
    <Stack spacing='16px'>
      <Typography variant='body2' color='text.secondary'>
        This closes your personal TechProOne account and signs you out everywhere.
        Brands you share with other owners keep running — this does not delete them.
      </Typography>

      {blocked && <Alert severity='warning' sx={{ borderRadius: '12px' }}>{blocked}</Alert>}

      <TextField
        size='small'
        label={`Type “${CONFIRM}” to confirm`}
        placeholder={CONFIRM}
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value)}
        disabled={busy}
        fullWidth
      />

      <Stack direction='row' justifyContent='flex-end'>
        <GlassButton
          variant='outlined'
          color='error'
          onClick={close}
          disabled={!matches || busy}
          disabledReason={matches ? '' : `Type ${CONFIRM} exactly to confirm.`}
          sx={{ borderRadius: '14px', px: '26px', height: 44 }}
        >
          {busy ? 'Closing…' : 'Close my account'}
        </GlassButton>
      </Stack>
    </Stack>
  );
}
